import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DarkModeToggle } from "../components/DarkModeToggle";
import LearnerBottomNav from "../components/learner/LearnerBottomNav";
import MyBookings from "./MyBookings";
import useConsumeSearchParam from "../hooks/useConsumeSearchParam";
import flashFocusElement from "../hooks/flashFocusElement";
import {
  CalendarDays, Repeat, Zap, Clock, ChevronRight, ArrowLeft,
} from "lucide-react";

/**
 * LearnerLessons — Phase Pl-C (Feb 7, 2026).
 *
 * Single bundled lessons/booking hub for pupils. Groups the four
 * existing booking flows behind one tab so the learner never has to
 * hunt across the home page:
 *
 *   • Single lesson      → /learn/book
 *   • Weekly recurring   → /learn/book/recurring
 *   • Last-minute slots  → /learn/last-minute
 *   • Waitlist           → /learn/waitlist
 *
 * Plus an "Upcoming" panel (re-uses the existing <MyBookings />)
 * so today's diary is one tap away.
 *
 * No backend changes. No route deletions. The legacy /learn/book
 * surface stays reachable directly. This page is purely a router.
 */
export default function LearnerLessons() {
  const nav = useNavigate();
  const { user } = useAuth();

  // Operational Continuity Convergence V1 (Feb 28, 2026) —
  // Break #1: receive the `?booking={id}` deep-link from
  // bookings notifications (booking_created, booking_rescheduled,
  // booking_request_accepted, lesson_payment_received, lesson_cancelled,
  // gap_claim_accepted) and gently scroll-to + flash the row inside
  // <MyBookings />. Param is consumed once on mount via
  // useConsumeSearchParam, then dropped from the URL so back/forward
  // does not re-fire the flash. See WorkMode's existing `?intent=`
  // handler for the canonical pattern.
  const focusBookingId = useConsumeSearchParam("booking");
  useEffect(() => {
    if (!focusBookingId) return;
    // Give <MyBookings /> a moment to fetch + render its rows.
    const t = window.setTimeout(
      () => flashFocusElement(`row-booking-${focusBookingId}`),
      400,
    );
    return () => window.clearTimeout(t);
  }, [focusBookingId]);

  const tiles = [
    {
      id: "single",
      testid: "lessons-tile-single",
      title: "Book a single lesson",
      body: "Pick a date and time on your instructor's diary.",
      icon: CalendarDays,
      accent: "blue",
      to: "/learn/book",
    },
    {
      id: "recurring",
      testid: "lessons-tile-recurring",
      title: "Weekly recurring lessons",
      body: "Same slot every week — set it once, keep your rhythm.",
      icon: Repeat,
      accent: "indigo",
      to: "/learn/book/recurring",
    },
    {
      id: "last-minute",
      testid: "lessons-tile-last-minute",
      title: "Last-minute slots",
      body: "Open slots in the next 72 hours near you.",
      icon: Zap,
      accent: "amber",
      to: "/learn/last-minute",
    },
    {
      id: "waitlist",
      testid: "lessons-tile-waitlist",
      title: "Join a waitlist",
      body: "Get notified when a slot opens with a fully-booked instructor.",
      icon: Clock,
      accent: "emerald",
      to: "/learn/waitlist",
    },
  ];

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            data-testid="lessons-back"
            onClick={() => nav("/learn")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 data-testid="lessons-title" className="text-lg font-extrabold text-slate-900 dark:text-white truncate">
              Your calendar
            </h1>
            <p className="text-xs text-slate-400 font-medium truncate">
              {(user?.founder_mode_subject_name || user?.name)
                ? `Hi ${(user.founder_mode_subject_name || user.name).split(" ")[0]} — `
                : ""}book, browse and manage your lessons
            </p>
          </div>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 mt-5 space-y-5">
        <section data-testid="lessons-upcoming">
          <MyBookings />
        </section>
        {/* Booking options follow the pupil's actual diary. */}
        <section data-testid="lessons-tiles" className="space-y-3">
          {tiles.map((t) => (
            <BookingTile key={t.id} {...t} onClick={() => nav(t.to)} />
          ))}
        </section>

      </div>

      <LearnerBottomNav active="work" />
    </div>
  );
}

const ACCENT_RING = {
  blue: "hover:border-blue-300 dark:hover:border-blue-700/50",
  indigo: "hover:border-indigo-300 dark:hover:border-indigo-700/50",
  amber: "hover:border-amber-300 dark:hover:border-amber-700/50",
  emerald: "hover:border-emerald-300 dark:hover:border-emerald-700/50",
};

const ACCENT_ICON_BG = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  indigo: "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
};

function BookingTile({ testid, title, body, icon: Icon, accent = "blue", onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-colors ${ACCENT_RING[accent]}`}
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${ACCENT_ICON_BG[accent]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-extrabold text-slate-900 dark:text-white truncate">{title}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{body}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 dark:text-slate-500 flex-shrink-0" />
    </button>
  );
}

